import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import { BnNgIdleService } from 'bn-ng-idle';
import { formatDate } from '@angular/common';
import { TsaService } from '../_services/tsa.service';
import 'chartjs-plugin-colorschemes'; // https://nagix.github.io/chartjs-plugin-colorschemes/colorchart.html
import {D3matrixComponent} from '../d3matrix/d3matrix.component';
import {D3pieComponent} from '../d3pie/d3pie.component';
import {D3circlesComponent} from '../d3circles/d3circles.component';
import {D3barComponent} from '../d3bar/d3bar.component';
import {ComparisonComponent} from '../comparison/comparison.component';
import {YoyComponent} from '../yoy/yoy.component';
import {TokenStorageService} from '../_services/token-storage.service';
import {Globals} from '../globals';
import 'chartjs-plugin-zoom';
import {MapComponent} from '../map/map.component';
import {UIChart} from 'primeng/chart';
import { GridComponent } from '../grid/grid.component';

interface Airport {
  code: string;
  name: string;
  city: string;
  state: string;
  display: string;
  order: number;
}

@Component({
  selector: 'app-root',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css']
})

export class MainComponent implements OnInit {
  // Use ViewChild to run child component's function
  @ViewChild(D3matrixComponent) d3matrix: D3matrixComponent | undefined;
  @ViewChild(D3pieComponent) d3pie: D3pieComponent | undefined;
  @ViewChild(D3circlesComponent) d3circles: D3circlesComponent | undefined;
  @ViewChild(D3barComponent) d3bar: D3barComponent | undefined;
  @ViewChild('benchmark') compBenchmark: ComparisonComponent | undefined;
  @ViewChild('comp1') comp1: ComparisonComponent | undefined;
  @ViewChild('comp7') comp7: ComparisonComponent | undefined;
  @ViewChild('comp30') comp30: ComparisonComponent | undefined;
  @ViewChild(YoyComponent) yoy: YoyComponent | undefined;
  @ViewChild(MapComponent) map: MapComponent | undefined;
  @ViewChild('airportInput') el: any;
  @ViewChild('chartTS') chartTS: UIChart | undefined;
  @ViewChild('chartHourly') chartHourly: UIChart | undefined;
  @ViewChild(GridComponent) grid: GridComponent | undefined;
  inputWComma = false;

  screenWidth: number = 500;  // window.innerWidth may be greater than actual screen width

  isLoggedIn = false;
  token = '';
  title = 'Airport Traffic';
  location = 'All US Airports';  // Location in chart title
  tabIndex = 0;

  latestUS = '2021-01-01';
  latestAirport = '2021-01-01';
  latestActual = '2021-01-01';

  // The TSA data starts from May 18, 2014
  minDate = new Date('2014-05-18');

  // The following dataset is for charts, which have different structure from the data for tables
  tsData: any = [];
  hourlyData: any = [];
  stackedHourlyChart: boolean = false;

  // Distribution data in Json
  distData: any = null;
  // Distribution chart types
  charts = [
    {code: 'map', name: 'Bubble Map'},
    {code: 'donut', name: 'Donut Chart'},
    {code: 'bar', name: 'Bar Chart'},
    {code: 'circle', name: 'Circle Packing'}
  ];
  selectedChart = {code: 'map', name: 'Bubble Map'};
  // Drop down list in the Comparison chart
  lines = [
    {code: '1', name: 'Daily Throughput'},
    {code: '7', name: '7-day Average'},
    {code: '30', name: '30-day Average'}
  ];
  selectedLine = {code: '7', name: '7-day Average'};
  bk = 'BENCHMARK';
  useDates = true;

  chartOptions: any;
  chartWidth = '90vw';
  chartHeight = '65vh';
  params: string = '';

  displayData: boolean = false; // True to show the data table
  tableCols: any = [];
  tableData: any = [];      // Active table data
  tableDataYOY: any = [];   // YOY table data
  tableDataTS: any = [];    // Time series table data
  tableDataHourly: any = [];// Hourly chart table data
  tableDataMatrix: any = [];// Hourly matrix table data
  tableDataDist: any = [];  // Airport distribution table data
  tableDataMap: any = [];   // Map data
  tableDataBenchmark: any = [];  // Airport benchmark table data
  tableDataComp: any = [];  // Airport comparison table data
  tableWidth = {width: '50vw'};
  tableName = 'data';
  tableLabel = '';
  windowWidth = {width: '50vw'};
  // default is half of the view width. Need to change the whole object to refresh the display.

  loading = false;
  selectedObjects: Airport[] = [];
  selectedAirports: string = '';
  filteredAirports: Airport[] = [];
  checkPoints: any[] = [];  // for all airports
  terminals: any[] = []; // for one airport
  selectedTerminal: string = '';

  startDate: Date = new Date(new Date('2023-01-01').getTime() + new Date('2023-01-01').getTimezoneOffset()*60000);
  // Need to use time zone offset in the same day. Otherwise, the daylight saving time may create a difference
  // Show 3 months from now
  endDate: Date = new Date(new Date().getTime() + new Date().getTimezoneOffset()*60000 + 91 * 24 * 3600 * 1000);
  startDateStr: string = '2023-01-01';
  endDateStr: string = '2025-01-01';
  interval: number = 7;
  benchmarkInterval: number = 900;

  /**
   * Set up default values
   * @param service1 - service for getting YOY chart data
   * @param serviceTS - service for getting time series chart data
   * @param serviceApt - service for getting airport list
   */
  constructor(private serviceApt: TsaService, private serviceLatest: TsaService,
              private service1: TsaService, private serviceTS: TsaService,
              private serviceHourly: TsaService, private serviceTable: TsaService,
              private tokenStorageService: TokenStorageService,
              private globals: Globals, private bnIdle: BnNgIdleService) {

    this.screenWidth = window.innerWidth;

    this.startDateStr = formatDate(this.startDate, 'yyyy-MM-dd', 'en-US');
    this.endDateStr = formatDate(this.endDate, 'yyyy-MM-dd', 'en-US');
    this.interval = Math.round((this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24));
    let w = 150 + Math.ceil(this.interval/364) * 120;
    this.tableWidth = { width: (w - 40) + 'px'};
    this.windowWidth = {width: w + 'px'};
    if (w > window.innerWidth) {
      this.windowWidth = { width: (window.innerWidth-20).toString() + 'px'}
    }
    this.getParams();

    // It takes a little bit to get latestUS from the back-end. So set it as yesterday first.
    let yesterday = new Date();
    yesterday.setDate(yesterday.getDate()-1);
    this.latestUS = formatDate(yesterday, 'yyyy-MM-dd', 'en-US');
    this.latestActual = this.latestUS;

    // Set up chart options
    this.chartOptions = this.globals.chartOptions;

    // This one is not impacted by user selection of starting and ending date
    this.benchmarkInterval = ((new Date().getTime() - new Date("2023-01-01").getTime())/1000/3600/24 + 30);
  }

  /**
   * Init function
   */
  ngOnInit(): void {
    // Get latest update dates (run this first)
    this.serviceLatest.url = '/Query.action?sql=SQL_TSA_LATEST_DATES';
    this.serviceLatest.getData().subscribe((data: any[]) => {
      for (let item of data) {
        if (item.NAME == 'latestUS') {
          this.latestUS = item.DATE;
        }
        if (item.NAME == 'latestAirport') {
          this.latestAirport = item.DATE;
        }
      }
      this.latestActual = this.latestUS;
    });

    // Test if user has been idle for 120 minutes
    this.bnIdle.startWatching(120*60).subscribe((isTimedOut: boolean) => {
      if (isTimedOut) {
        this.tokenStorageService.signOut();
        window.location.replace('/tsa');
      }
    });

    this.token = this.tokenStorageService.getUser().accessToken;
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if(this.isLoggedIn) {
      // Get Airport List
      this.globals.airports = [];
      this.serviceApt.url = '/Query.action?sql=SQL_TSA_AIRPORTS&limit=-1';
      this.serviceApt.getData().subscribe((data: any[]) => {
        // Generate unique airport list
        let flags: boolean[] = [];
        for (let item of data) {
          this.checkPoints.push(item);
          if (flags[item.AIRPORT]) continue;
          flags[item.AIRPORT] = true;

          // Some city has a comma at the end
          let city = item.CITY.trim();
          if (city.substr(city.length - 1) == ',') city = city.substr(0, city.length - 1);
          let display = item.AIRPORT + ': ' + city;
          if(item.STATE) display += ', ' + item.STATE;
          let apt: Airport = {
            code: item.AIRPORT,
            name: item.AIRPORT_NAME,
            city: city,
            state: item.STATE,
            display: display,
            order: 0
          }
          this.globals.airports.push(apt);
          this.filteredAirports.push(apt);
        }
      });

    }
  }

  /**
   * Refresh data for the time series chart
   * @param url - URL for getting the data
   */
  updateServiceTS(url: string): void {
    this.serviceTS.url = url;
    this.loading = true;
    this.serviceTS.getData().subscribe((data: any[]) => {
      this.loading = false;
      this.tableDataTS = data;
      const x = [];
      const y1 = [];
      const y2 = [];
      const y3 = [];
      const est1 = [];
      const est2 = [];
      const est3 = [];

      // Use null when we don't have input data; use zero when the checkpoint is closed
      let fillValue = null;
      // if (this.selectedTerminal.length > 0) fillValue = 0;
      let prevDate = '2200-01-01';
      for (let item of data) {
        let interDays = (new Date(item.DATE).getTime() - new Date(prevDate).getTime()) / (1000 * 3600 * 24);
        if (interDays > 1.1) {
          // Fill zeros if the date is not continuous
          for (let i=new Date(prevDate).getTime();
               i<new Date(item.DATE).getTime(); i += 1000 * 3600 * 24) {
            x.push(formatDate(i, "yyyy-MM-dd", 'en-US'));
            y1.push(fillValue);
            y2.push(fillValue);
            y3.push(fillValue);
            est1.push(null);
            est2.push(null);
            est3.push(null);
          }
        }
        prevDate = item.DATE;
        x.push(item.DATE);
        let cutoffDate: string = this.selectedAirports.length == 0 ? this.latestUS : this.latestAirport;
        if(item.DATE >= cutoffDate) {
          est1.push(item.THROUGHPUT);
          est2.push(item.AVG7);
          est3.push(item.AVG30);
          // Add one point to connect two lines
          if(item.DATE > cutoffDate) {
            y1.push(null);
            y2.push(null);
            y3.push(null);
          } else {
            y1.push(item.THROUGHPUT);
            y2.push(item.AVG7);
            y3.push(item.AVG30);
          }
        } else {
          y1.push(item.THROUGHPUT);
          y2.push(item.AVG7);
          y3.push(item.AVG30);
          est1.push(null);
          est2.push(null);
          est3.push(null);
        }
      }
      let color0 = this.globals.getColor(0);
      let color1 = this.globals.getColor(1);
      let color2 = this.globals.getColor(2);
      this.tsData = {
        labels: x,
        datasets: [
          {
            label: '30-day Average',
            fill: false,
            data: y3
          },
          {
            label: '7-day Average',
            fill: false,
            data: y2
          },
          {
            label: 'Daily Throughput',
            fill: false,
            data: y1
          },
          {
            label: 'Forecast 30-day Average',
            fill: false,
            borderColor: color0,
            pointBorderColor: color0,
            pointBackgroundColor: color0,
            pointHoverBorderColor: color0,
            pointHoverBackgroundColor: color0,
            data: est3
          },
          {
            label: 'Forecast 7-day Average',
            borderColor: color1,
            pointBorderColor: color1,
            pointBackgroundColor: color1,
            pointHoverBorderColor: color1,
            pointHoverBackgroundColor: color1,
            fill: false,
            data: est2
          },
          {
            label: 'Forecast Throughput',
            borderColor: color2,
            pointBorderColor: color2,
            pointBackgroundColor: color2,
            pointHoverBorderColor: color2,
            pointHoverBackgroundColor: color2,
            fill: false,
            data: est1
          }
        ]
      };

      // Show points when there are less than 10 data points. Otherwise, single day data won't display.
      this.globals.dataPoints(this.chartTS, x.length);

    });
  }


  /**
   * Change fill style of the hourly chart data
   */
  changeFillStyle(data: any): void {
    let fill = this.stackedHourlyChart;
    const x = [];
    const y1 = [];
    const y2 = [];
    const y3 = [];
    const y4 = [];
    const y5 = [];
    const y6 = [];
    const y7 = [];
    for (const item of data) {
      x.push(item.HOUR);
      y1.push(item.D1);
      y2.push(item.D2);
      y3.push(item.D3);
      y4.push(item.D4);
      y5.push(item.D5);
      y6.push(item.D6);
      y7.push(item.D7);
    }
    this.hourlyData = {
      labels: x,
      datasets: [
        {
          label: 'Mon',
          fill: fill,
          data: y1
        },
        {
          label: 'Tue',
          fill: fill,
          data: y2
        },
        {
          label: 'Wed',
          fill: fill,
          data: y3
        },
        {
          label: 'Thu',
          fill: fill,
          data: y4
        },
        {
          label: 'Fri',
          fill: fill,
          data: y5
        },
        {
          label: 'Sat',
          fill: fill,
          data: y6
        },
        {
          label: 'Sun',
          fill: fill,
          data: y7
        }
      ]
    };
  }

  /**
   * Refresh data for the hourly chart
   * @param url - URL for getting the data
   */
  updateServiceHourly(url: string): void {
    this.serviceHourly.url = url;
    this.loading = true;
    this.serviceHourly.getData().subscribe((data: any[]) => {
      this.loading = false;
      this.tableDataHourly = data;
      this.changeFillStyle(data);
    });
  }


  /**
   * Show data in a table
   */
  showData(): void {
    this.loading = true;
    this.setTableWidth();

    // Wait a little bit to show the loading spinner
    setTimeout(()=>{
      switch(this.tabIndex) {
        case 0:
          this.tableCols = [
            {field: 'DATE', header: 'Date'}
          ];
          for(let i=2014; i<=this.endDate.getFullYear(); i++) {
            if(this.startDate.getFullYear() <= i && i <= this.endDate.getFullYear()) {
              this.tableCols.push(
                {field: 'Y'+i.toString(), header: i.toString()},
              );
            }
          }
          this.tableData = this.tableDataYOY;

          // Need to put this in each block because some of them have a delay
          this.displayData =  true;
          this.loading = false;
          break;
        case 1:
          this.tableCols = [
            {field: 'DATE', header: 'Date'},
            {field: 'THROUGHPUT', header: 'Daily Throughput'},
            {field: 'AVG7', header: '7-day Average'},
            {field: 'AVG30', header: '30-day Average'}
          ];
          this.tableData = this.tableDataTS;
          this.displayData =  true;
          this.loading = false;
          break;
        case 2:
          this.tableCols = [
            {field: 'HOUR', header: 'Hour'},
            {field: 'D1', header: 'Mon'},
            {field: 'D2', header: 'Tue'},
            {field: 'D3', header: 'Wed'},
            {field: 'D4', header: 'Thu'},
            {field: 'D5', header: 'Fri'},
            {field: 'D6', header: 'Sat'},
            {field: 'D7', header: 'Sun'},
          ];
          this.tableData = this.tableDataHourly;
          this.displayData =  true;
          this.loading = false;
          break;
        case 3:
          this.tableCols = [
            {field: 'DATE', header: 'Date'},
            {field: 'H00', header: '00:00'},
            {field: 'H01', header: '01:00'},
            {field: 'H02', header: '02:00'},
            {field: 'H03', header: '03:00'},
            {field: 'H04', header: '04:00'},
            {field: 'H05', header: '05:00'},
            {field: 'H06', header: '06:00'},
            {field: 'H07', header: '07:00'},
            {field: 'H08', header: '08:00'},
            {field: 'H09', header: '09:00'},
            {field: 'H10', header: '10:00'},
            {field: 'H11', header: '11:00'},
            {field: 'H12', header: '12:00'},
            {field: 'H13', header: '13:00'},
            {field: 'H14', header: '14:00'},
            {field: 'H15', header: '15:00'},
            {field: 'H16', header: '16:00'},
            {field: 'H17', header: '17:00'},
            {field: 'H18', header: '18:00'},
            {field: 'H19', header: '19:00'},
            {field: 'H20', header: '20:00'},
            {field: 'H21', header: '21:00'},
            {field: 'H22', header: '22:00'},
            {field: 'H23', header: '23:00'}
          ];
          if (this.tableDataMatrix.length == 0) {
            this.serviceTable.url = '/Query.action?sql=SQL_HOURLY_TRAFFIC_TABLE' + this.params;
            this.serviceTable.getData().subscribe((data: any[]) => {
              this.tableDataMatrix = data;
              this.tableData = this.tableDataMatrix;
              this.displayData =  true;
              this.loading = false;
            });
          } else {
            this.tableData = this.tableDataMatrix;
            this.displayData =  true;
            this.loading = false;
          }
          break;
        case 4:
          if (this.selectedChart.code=="map") {
            this.tableCols = [
              {field: 'CODE', header: 'Airport Code'},
              {field: 'NAME', header: 'Airport Name'},
              {field: 'CITY', header: 'City Name'},
              {field: 'STATE', header: 'State'},
              {field: 'SIZE', header: 'Size'},
              {field: 'LONGITUDE', header: 'Longitude'},
              {field: 'LATITUDE', header: 'Latitude'}
            ];
            if (this.tableDataMap.length == 0) {
              this.serviceTable.url = '/Query.action?sql=SQL_TSA_AIRPORT_TABLE' + this.params;
              this.serviceTable.getData().subscribe((data: any[]) => {
                this.tableDataMap = data;
                this.tableData = this.tableDataMap;
                this.displayData =  true;
                this.loading = false;
              });
            } else {
              this.tableData = this.tableDataMap;
              this.displayData =  true;
              this.loading = false;
            }
          } else {
            this.tableCols = [
              {field: 'AIRPORT', header: 'Airport'},
              {field: 'CHECKPOINT', header: 'CheckPoint'},
              {field: 'PAX_CHECKPOINT', header: 'Checkpoint Traffic'},
              {field: 'PAX_AIRPORT', header: 'Airport Traffic'}
            ];
            if (this.tableDataDist.length == 0) {
              this.serviceTable.url = '/Query.action?sql=SQL_TSA_AIRPORT_CHECKPOINT' + this.params;
              this.serviceTable.getData().subscribe((data: any[]) => {
                this.tableDataDist = data;
                this.tableData = this.tableDataDist;
                this.displayData =  true;
                this.loading = false;
              });
            } else {
              this.tableData = this.tableDataDist;
              this.displayData =  true;
              this.loading = false;
            }
          }
          break;
        case 5:
          this.tableCols = [
            {field: 'DATE', header: 'Date'},
            {field: 'US', header: 'US Total'}
          ];
          for(let obj of this.selectedObjects) {
            this.tableCols.push({field: obj.code, header: obj.code})
          }
          this.tableData = this.tableDataBenchmark;
          this.displayData =  true;
          this.loading = false;
          break;
        case 6:
          this.tableCols = [
            {field: 'DATE', header: 'Date'}
          ];
          for(let obj of this.selectedObjects) {
            this.tableCols.push({field: obj.code, header: obj.code})
          }
          this.tableData = this.tableDataComp;
          this.displayData =  true;
          this.loading = false;
          break;
      }
    }, 100)
  }

  /**
   * Download the chart data into a CSV file
   */
  csvDownload(): void {
    if(this.grid) this.grid.csvDownload();
  }

  /**
   * Determine table width
   */
  setTableWidth(): void {
    let w = 300;
    switch(this.tabIndex) {
      case 0:
        w = 100 + Math.ceil(this.interval/364) * 120;
        this.tableName = "Year_Over_Year";
        break;
      case 1:
        w = 500;
        this.tableName = "Time_Series";
        break;
      case 2:
        w = 800;
        this.tableName = "Hourly_Curve";
        break;
      case 3:
        w = 1650;
        this.tableName = "Hourly_Heatmap";
        break;
      case 4:
        if(this.selectedChart.code=='map') {
          w = 800;
        } else {
          w = 600;
        }
        this.tableName = "Distribution";
        break;
      case 5:
        w = 250 + Math.ceil(this.selectedAirports.length/4) * 100;
        if (w < 300) w = 300; // For US total
        this.tableName = "Benchmark";
        break;
      case 6:
        w = 150 + Math.ceil(this.selectedAirports.length/4) * 100;
        this.tableName = "Comparison";
        break;
    }
    this.tableLabel = this.tableName.replace(/_/g,' ');
    if(w < 350) w = 350;
    this.tableWidth = { width: (w - 40) + 'px'};
    this.windowWidth = { width: w + 'px'};
    if (w > this.screenWidth) {
      this.windowWidth = { width: (this.screenWidth-20).toString() + 'px'}
    }
  }


  /**
   * Actions when tab panel changes
   * @param e
   */
  tabChange(e: any): void {
    this.tabIndex = e.index;
    this.displayData =  false;
    this.useDates = true;
    let params = this.getParams();
    if(e.index == 5) this.location = this.selectedAirports;
    this.setChartTitle();
    this.updateLatestActual();
    this.setTableWidth();
    this.chartOptions.scales.xAxes[0].scaleLabel.labelString = 'Date';
    this.chartOptions.scales.xAxes[0].type = 'category';
    this.chartOptions.scales.yAxes[0].stacked = false;  // Regular line chart except hourly chart
    this.chartOptions.plugins.zoom.zoom.speed = 0.01;
    switch(e.index) {
      case 0:
        this.chartOptions.scales.xAxes[0].type = 'time';
        this.chartOptions.plugins.zoom.zoom.speed = 0.01;
        if(this.yoy != null) {
          if(this.tableDataYOY === null) {
            this.yoy.updateService('/Query.action?sql=SQL_TSA_YOY_THROUGHPUT' + this.selectedLine.code + this.params);
          } else {
            if (this.yoy.chart) this.yoy.chart.reinit();
          }
        }
        break;
      case 1:
        this.chartOptions.scales.xAxes[0].type = 'time';
        this.chartOptions.plugins.zoom.zoom.speed = 0.00005 * this.interval;
        if(this.tsData.length == 0) {
          this.updateServiceTS('/Query.action?sql=SQL_TSA_THROUGHPUT' + params);
        } else {
          if(this.chartTS) this.chartTS.reinit();
        }
        break;
      case 2:
        this.chartOptions.scales.xAxes[0].scaleLabel.labelString = 'Hour';
        this.chartOptions.scales.yAxes[0].stacked = this.stackedHourlyChart;
        if(this.hourlyData.length == 0) {
          this.updateServiceHourly('/Query.action?sql=SQL_HOURLY_TRAFFIC7' + params);
        } else {
          if(this.chartHourly) this.chartHourly.reinit();
        }
        break;
      case 3:
        if(this.d3matrix != null) {
          if(this.d3matrix.data.length == 0) {
            this.d3matrix.showD3(this.params, true);
          }
        }
        break;
      case 4:
        if(this.selectedChart.code=='map') {
          if(this.map != null) {
            if (this.distData === null) {
              this.map.showCircles(this.params);
            }
          }
        } else {
          if (this.d3pie != null) {
            if (this.distData === null) {
              this.d3pie.showD3(this.params);
            } else {
              setTimeout(() => {
                if (this.d3pie) this.d3pie.onResize();
              }, 100)
            }
          }
          if (this.d3bar != null) {
            if (this.distData === null) {
              this.d3bar.showD3(this.params);
            } else {
              setTimeout(() => {
                if (this.d3bar) this.d3bar.onResize();
              }, 100)
            }
          }
          if (this.d3circles != null) {
            if (this.distData === null) {
              this.d3circles.showD3(this.params);
            }
          }
        }
        break;
      case 5:
        this.chartOptions.scales.xAxes[0].type = 'time';
        this.chartOptions.datasets.line.pointRadius = 0;
        this.chartOptions.plugins.zoom.zoom.speed = 0.00005 * this.benchmarkInterval;
        if(this.compBenchmark != null) {
          if(this.tableDataBenchmark === null) {
            this.compBenchmark.updateService('/Query.action?sql=SQL_TSA_BENCHMARK' + this.params);
          } else {
            if (this.compBenchmark.chart) this.compBenchmark.chart.reinit();
          }
        }
        this.useDates = false;
        break;
      case 6:
        this.chartOptions.scales.xAxes[0].type = 'time';
        this.chartOptions.datasets.line.pointRadius = 0;
        this.chartOptions.plugins.zoom.zoom.speed = 0.00005 * this.interval;
        if(this.comp1 != null || this.comp7 != null || this.comp30 != null) {
          if(this.tableDataComp === null) {
            this.updateComp();
          } else {
            if (this.comp1 && this.comp1.chart) this.comp1.chart.reinit();
            if (this.comp7 && this.comp7.chart) this.comp7.chart.reinit();
            if (this.comp30 && this.comp30.chart) this.comp30.chart.reinit();
          }
        }
        break;
    }

  }

  /**
   * Update one of the comparison charts
   */
  updateComp(): void {
    let comp = this.comp1;
    if(this.selectedLine.code == '7') comp = this.comp7;
    if(this.selectedLine.code == '30') comp = this.comp30;
    if (comp != null) {
      comp.updateService('/Query.action?sql=SQL_TSA_AIRPORT_TRAFFIC' + this.selectedLine.code + this.params);
    }
  }

  /**
   * Use typeahead to filter airports
   * @param event - event triggered
   */
  filterAirport(event: any): void {
    let filtered: any[] = [];
    let queries = event.query.split(",");
    for (let i = 0; i < this.globals.airports.length; i++) {
      let item = this.globals.airports[i];
      if(queries.length > 1) {
        // search code only
        for (let query of queries) {
          query = query.trim();
          if (query.length>=3) {
            if (item.display.toLowerCase().substr(0,3) == query.toLowerCase()) {
              item.order = 0;
              filtered.push(item);
            }
          }
        }
      } else {
        // can search name
        let query = queries[0];
        let idx = item.display.toLowerCase().indexOf(query.toLowerCase());
        let idx2 = item.display.indexOf(query);
        if (idx >= 0) {
          if (idx2 == 0) item.order = 0;
          else if (idx == 0) item.order = 1;
          else if (idx2 > 0) item.order = 2;
          else item.order = 3;
          filtered.push(item);
        }
      }
    }
    // Order by Code first and then by name
    this.filteredAirports = filtered.sort(function(a,b){return a.order-b.order});
  }

  /**
   * Get URL params
   */
  getParams(): string {
    this.params = '&dst=' + this.selectedAirports + '&field=' + this.selectedTerminal
      + '&startDate=' + this.startDateStr + '&endDate=' + this.endDateStr;
    return this.params;
  }

  resetData(index: number): void {
    if(index !== 0) this.tableDataYOY = null;
    if(index !== 1) this.tsData = [];
    if(index !== 2) this.hourlyData = [];
    if(index !== 3 && this.d3matrix != null) {
      this.d3matrix.data = [];
      this.tableDataMatrix = [];
    }
    if(index !== 4) {
      this.distData = null;
      if(this.map != null) this.map.clearData();
      this.tableDataDist = [];
      this.tableDataMap = [];
    }
    if(index !== 5) this.tableDataBenchmark = null;
    if(index !== 6) this.tableDataComp = null;
  }

  /**
   * Refresh the chart
   */
  refreshChart(): void {
    let params = this.getParams();
    this.updateLatestActual();
    switch(this.tabIndex) {
      case 0:
        if(this.yoy != null) {
          this.yoy.updateService('/Query.action?sql=SQL_TSA_YOY_THROUGHPUT' + this.selectedLine.code + params);
        }
        break;
      case 1:
        this.updateServiceTS('/Query.action?sql=SQL_TSA_THROUGHPUT' + params);
        break;
      case 2:
        this.updateServiceHourly('/Query.action?sql=SQL_HOURLY_TRAFFIC7' + params);
        break;
      case 3:
        if(this.d3matrix != null) {
          this.d3matrix.showD3(this.params, true);
          this.tableDataMatrix = [];  // Empty the table data so that user has a chance to refresh it
        }
        break;
      case 4:
        if(this.d3pie != null) {
          this.d3pie.showD3(this.params);
        }
        if(this.d3circles != null) {
          this.d3circles.showD3(this.params);
        }
        if(this.d3bar != null) {
          this.d3bar.showD3(this.params);
        }
        if(this.map != null) {
          this.map.clearData();
          this.tableDataMap = [];
          this.map.showCircles(this.params);
        }
        break;
      case 5:
        if (this.compBenchmark != null) {
          this.compBenchmark.updateService('/Query.action?sql=SQL_TSA_BENCHMARK' + this.params);
        }
        break;
      case 6:
        this.updateComp();
        break;
    }
    this.setTableWidth();
    this.resetData(this.tabIndex);
  }

  /**
   * Action when an airport is selected
   * @param obj - selected Airport object
   */
  changeSelection(obj:any): void {
    this.terminals = [];
    if (this.selectedObjects.length == 1) {
      let obj = this.selectedObjects[0];
      this.selectedAirports = obj.code;
      this.location = obj.display;

      if(!this.selectedAirports.endsWith("*")) {
        for (let item of this.checkPoints) {
          if (item.AIRPORT == obj.code) this.terminals.push(item);
        }
      }
    } else {
      this.selectedAirports = '';
      for (let i=0; i<this.selectedObjects.length; i++) {
        this.selectedAirports += this.selectedObjects[i].code;
        if (i<this.selectedObjects.length-1) this.selectedAirports += ',';
      }
      this.location = this.selectedAirports.length == 0 ? 'All US Airports' : this.selectedAirports;
    }
    this.setChartTitle();
    this.selectedTerminal = '';
    this.inputWComma = false;
  }

  setChartTitle(): void {
    if (!this.location) this.location = 'All US Airports';
    if (this.tabIndex == 5) {
      if (window.innerWidth < 500) {
        this.chartOptions.title.text = '7-day Avg Over 2019 at ' + this.location;
      } else {
        this.chartOptions.title.text = '7-day Average Throughput Over 2019 Level at ' + this.location;
      }
    } else {
      this.chartOptions.title.text = 'Passenger Throughput at ' + this.location;
    }
  }

  selectAirport(obj:any): void {
    if(this.el.suggestions != null && this.inputWComma) {
      for(let s of this.el.suggestions) {
        if (!this.selectedObjects.includes(s)) this.selectedObjects.push(s);
      }
    }
    this.changeSelection(obj);
    this.refreshChart();
  }

  airportKeyUp(obj:any): void {
    if (obj.target.value.indexOf(",")>0) this.inputWComma = true;
  }

  unselectAirport(obj:any): void {
    this.changeSelection(obj);
    if (this.selectedAirports.length == 3 && this.tabIndex == 6) {
      this.tabIndex = 1;  // Change to TimeSeries tab because Comparison tab will disappear
      this.selectedAirports = '';
    }
    this.refreshChart();
  }

  clearAirports(): void {
    this.selectedObjects = [];
    this.terminals = [];
    this.selectedAirports = '';
    this.location = 'All US Airports';
    this.setChartTitle();
    if (this.tabIndex == 6) {
      this.tabIndex = 1;  // Change to TimeSeries tab because Comparison tab will disappear
    }
    this.refreshChart();
  }

  /**
   * Action when a terminal is selected
   * @param obj - selected terminal
   */
  selectTerminal(obj:any): void {
    if(obj.value == null) {
      if (this.selectedTerminal.length > 0) {
        this.selectedTerminal = '';
        this.location = this.selectedAirports;
        this.chartOptions.title.text = 'Passenger Throughput at ' + this.selectedAirports;
        this.refreshChart();
      }
    } else {
      if (this.selectedTerminal != obj.value.CHECKPOINT) {
        this.selectedTerminal = obj.value.CHECKPOINT;
        this.location = this.selectedTerminal + '@' + this.selectedAirports;
        this.chartOptions.title.text = 'Passenger Throughput at ' + this.location;
        this.refreshChart();
      }
    }
  }

  /**
   * Action when either starting date or ending date is selected
   * @param obj - selected Airport object
   */
  selectDate(obj:any): void {
    if(this.startDateStr != formatDate(this.startDate, 'yyyy-MM-dd', 'en-US') ||
      this.endDateStr != formatDate(this.endDate, 'yyyy-MM-dd', 'en-US')) {
      if (this.startDate.getTime() > this.endDate.getTime()) {
        let tmpDate = this.startDate;
        this.startDate = this.endDate;
        this.endDate = tmpDate;
      }
      this.startDateStr = formatDate(this.startDate, 'yyyy-MM-dd', 'en-US');
      this.endDateStr = formatDate(this.endDate, 'yyyy-MM-dd', 'en-US');
      this.interval = Math.round((this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24));
      this.refreshChart();
    }
  }

  /**
   * Change period for charts
   * @param days
   */
  changePeriod(days: number): void {
    this.startDate = new Date(this.startDate.getTime() + days * 24 * 3600000);
    this.endDate = new Date(this.endDate.getTime() + days * 24 * 3600000);
    this.startDateStr = formatDate(this.startDate, 'yyyy-MM-dd', 'en-US');
    this.endDateStr = formatDate(this.endDate, 'yyyy-MM-dd', 'en-US');
    this.refreshChart();
  }

  /**
   * Move the period backward
   */
  prevPeriod(): void {
    this.changePeriod(-this.interval);
  }

  /**
   * Move the period forward
   */
  nextPeriod(): void {
    this.changePeriod(this.interval);
  }

  /**
   * Change interval days between starting date and ending date
   * @param event
   */
  changeInterval(event: any): void {
    this.endDate = new Date(this.startDate.getTime() + event.value * 24 * 3600000);
    this.startDateStr = formatDate(this.startDate, 'yyyy-MM-dd', 'en-US');
    this.endDateStr = formatDate(this.endDate, 'yyyy-MM-dd', 'en-US');
  }

  /**
   * Pass data from distribution child component to parent component
   * @param data
   */
  updateDistData(data: any): void {
    this.distData = data;
  }

  /**
   * Pass data from comparison child component to parent component
   * @param data
   */
  updateCompData(data: any): void {
    this.tableDataComp = data;
  }

  /**
   * Pass data from benchmark child component to parent component
   * @param data
   */
  updateBenchmarkData(data: any): void {
    this.tableDataBenchmark = data;
  }

  /**
   * Pass data from YOY child component to parent component
   * @param data
   */
  updateYOYData(data: any): void {
    this.tableDataYOY = data;
  }

  /**
   * Change hourly chart to stacked areas or lines
   * @param event
   */
  changeHourlyChart(event: any) {
    this.chartOptions.scales.yAxes[0].stacked = this.stackedHourlyChart;
    this.changeFillStyle(this.tableDataHourly);
  }

  /**
   * Update the latest actual date
   */
  updateLatestActual() {
    this.latestActual = this.latestUS;
    // All airport specific or hourly data
    if (this.selectedAirports.length > 0 || this.tabIndex == 2 || this.tabIndex == 3 || this.tabIndex == 4 || this.tabIndex == 6)
      this.latestActual = this.latestAirport;
  }

  // @HostListener('window:resize', ['$event'])
  // onResize(event: any) {
  //
  // }

  /**
   * Enable or disable zoom and pan function
   */
  changeZoomPan() {
    this.chartOptions.plugins.zoom.zoom.enabled = !this.chartOptions.plugins.zoom.zoom.enabled;
    this.chartOptions.plugins.zoom.pan.enabled = !this.chartOptions.plugins.zoom.pan.enabled;
    if (this.tabIndex == 1 && this.chartTS) {
      this.chartOptions.plugins.zoom.zoom.speed = 0.00005 * this.interval;
      this.chartTS.reinit();
    } else if (this.chartHourly) {
      this.chartOptions.plugins.zoom.zoom.speed = 0.01;
      this.chartHourly.reinit();
    }
  }
}
